import React, { useState } from 'react';
import { Stethoscope, GraduationCap, Award, Phone, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import ProviderPhoto from '../components/ProviderPhoto';
import ScheduleModal from '../components/ScheduleModal';

const Providers = () => {
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const providers = [
    {
      name: 'Dr. Concepcion Anayas',
      credentials: 'M.D.',
      specialty: 'Pediatrics',
      ageGroup: 'Newborn through Adolescent',
      image: 'https://customer-assets.emergentagent.com/job_new-cmcwv/artifacts/7gjbdm6j_Dr%20C%20AI.jpg',
      seoTitle: 'Dr. Concepcion Anayas, M.D. - Pediatrician in DeLand and Orange City FL',
      bio: [
        'Dr. C Anayas is a founding partner and the Head of Pediatrics of Community Medical Center of West Volusia. She is Board-Certified by the American Board of Pediatrics. Dr. C. Anayas graduated from Cebu Institute of Medicine in the Philippines.',
        'She did her residency training in Pediatrics at Mt. Carmel Mercy Hospital and completed her Internship at South Macomb Hospital and Detroit Memorial Hospital in Michigan.',
        'She co-founded Missionaries of Hope and has been involved in rural healthcare since 1975. She co-authored a poster presentation on "A Comprehensive Approach to Address Healthcare Providers\' Shortage in the US" during the 2011 Florida Medical Association\'s Annual convention in Orlando, Florida.',
        'She is a member of the American Academy of Pediatrics, Florida Pediatric Society and the Florida Medical Association. Dr. Concepcion Anayas provides expert pediatric care to children from birth through adolescence in DeLand, Orange City, Deltona, and throughout West Volusia County.'
      ]
    },
    {
      name: 'Dr. Marcelo Anayas',
      credentials: 'M.D.',
      specialty: 'Internal Medicine',
      ageGroup: 'Adults (18+)',
      image: 'https://customer-assets.emergentagent.com/job_new-cmcwv/artifacts/k257skf3_Dr%20M.jpg',
      seoTitle: 'Dr. Marcelo Anayas, M.D. - Internal Medicine Physician in DeLand FL',
      bio: [
        'Dr. M. Anayas graduated from Cebu Institute of Medicine in 1975. He completed his residency and internship at Bon Secours Hospital, in Michigan. His practice has been predominantly focused in serving the underserved areas and providing healthcare to the rural population.',
        'He is a founding partner and Medical Director of the Community Medical Center of West Volusia. The founding President of Missionaries of Hope, a non-profit organization and medical missionary devoted to the well-being, education and spiritual growth, of those in need in the underprivileged areas.',
        'He is a front-runner in the campaign to eliminate the disparities in the healthcare system. Especially on healthcare professional shortages in the underserved areas. Dr. M. Anayas gave a speech during the 2008-2010 National Summit of Clinicians for Healthcare Justice in Washington, DC.',
        'Dr. M. Anayas is a member of the Association of Clinicians for the Underserved, Florida Medical Association and the American Medical Association. He provides comprehensive primary care to adult patients (18 and older) in DeLand, Orange City, and West Volusia.'
      ]
    },
    {
      name: 'Pam Perry',
      credentials: 'ARNP, DNP',
      specialty: 'Pediatrics',
      ageGroup: 'Newborn through Adolescent',
      image: 'https://customer-assets.emergentagent.com/job_new-cmcwv/artifacts/a2wxig6b_Pam%20AI%20Final.jpg',
      seoTitle: 'Pam Perry, ARNP - Pediatric Nurse Practitioner in DeLand FL',
      bio: [
        'Pam Perry, ARNP graduated Magna Cum Laude from Bethune-Cookman University with a Bachelor\'s degree in Nursing. She completed both her Master of Science in Nursing and her Doctorate of Nursing Practice at the University of Florida in Gainesville, with special focus on Childhood Obesity and Children Vaccinations.',
        'She is currently the Supervising Pediatric Nurse Practitioner of Community Medical Center and does preceptorship with affiliated educational institutions like UF, UCF, BCU and many others.',
        'She is Board-Certified by the Pediatric National Certification Board and is an active member of the Florida Nurses Association, the National Association of Pediatric Nurse Practitioners (NAPNAP), the National Pediatric Asthma Trainer (NPAT), the American Nurses Association and the Reach-Out and Read Special Interest Group.',
        'Pam Perry, ARNP provides compassionate pediatric care to children from newborns through adolescents throughout DeLand, Orange City, and West Volusia County, Florida.'
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-24 lg:pt-28">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-700 to-teal-600 text-white py-20 lg:py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img 
            src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&q=85" 
            alt="Healthcare providers in West Volusia Florida" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-4xl lg:text-5xl font-extrabold mb-6">Meet Our Healthcare Providers</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Board-certified physicians and nurse practitioners serving DeLand, Orange City, and West Volusia County families with compassionate care
          </p>
        </div>
      </section>

      {/* Providers Section */}
      <section className="py-20 lg:py-28 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">Our Medical Team</h2>
            <p className="text-lg text-gray-600">Pediatrics for children • Internal Medicine for adults</p>
          </div>
          <div className="space-y-20 lg:space-y-28">
            {providers.map((provider, index) => (
              <article key={index} data-testid={`provider-article-${index}`} className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-start ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
                <div className={`${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="relative">
                    {/* Provider Photo - NO WHITE FRAME, uses cover fit */}
                    <div className="rounded-2xl shadow-2xl overflow-hidden aspect-[4/5] max-w-md mx-auto lg:mx-0">
                      <img 
                        src={provider.image} 
                        alt={provider.seoTitle}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute -bottom-6 left-4 right-4 lg:left-6 lg:right-6 bg-white rounded-xl shadow-xl p-4">
                      <div className="flex items-center gap-4">
                        <div className="bg-blue-100 text-blue-700 p-3 rounded-xl">
                          <Stethoscope size={28} aria-hidden="true" />
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">{provider.specialty}</p>
                          <p className="text-sm text-blue-600 font-semibold">{provider.ageGroup}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''} mt-12 lg:mt-0`}>
                  <header className="mb-8">
                    <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 mb-2">{provider.name}, {provider.credentials}</h2>
                    <p className="text-xl text-blue-700 font-bold">{provider.specialty}</p>
                    <p className="text-gray-500 mt-2">Patients: {provider.ageGroup} • Serving DeLand & Orange City</p>
                  </header>
                  <div className="space-y-5">
                    {provider.bio.map((paragraph, idx) => (
                      <p key={idx} className="text-gray-700 leading-relaxed text-lg">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                  <div className="mt-8 flex flex-wrap gap-4">
                    <div className="flex items-center gap-2 bg-blue-50 px-5 py-3 rounded-xl">
                      <GraduationCap className="text-blue-700" size={22} aria-hidden="true" />
                      <span className="text-gray-800 font-semibold">Board Certified</span>
                    </div>
                    <div className="flex items-center gap-2 bg-teal-50 px-5 py-3 rounded-xl">
                      <Award className="text-teal-700" size={22} aria-hidden="true" />
                      <span className="text-gray-800 font-semibold">Experienced</span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-24 px-4 bg-gradient-to-br from-blue-700 to-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-extrabold mb-6">Schedule an Appointment</h2>
          <p className="text-xl mb-10 text-blue-200">
            Dr. Concepcion Anayas, M.D., Dr. Marcelo Anayas, M.D., and Pam Perry, ARNP are ready to serve your family
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowScheduleModal(true)}
              data-testid="providers-schedule-btn"
              className="inline-flex items-center justify-center bg-white text-blue-700 px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-blue-50 transition-all cursor-pointer"
            >
              Schedule Online
            </button>
            <a
              href="tel:386-775-1792"
              data-testid="providers-call-oc-btn"
              className="inline-flex items-center justify-center bg-blue-800 border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-900 transition-all"
            >
              <Phone className="mr-2" size={22} />
              Orange City: (386) 775-1792
            </a>
            <a
              href="tel:386-738-1792"
              data-testid="providers-call-dl-btn"
              className="inline-flex items-center justify-center bg-blue-800 border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-900 transition-all"
            >
              <Phone className="mr-2" size={22} />
              DeLand: (386) 738-1792
            </a>
          </div>
        </div>
      </section>

      <ScheduleModal isOpen={showScheduleModal} onClose={() => setShowScheduleModal(false)} />
    </div>
  );
};

export default Providers;
